/**
 * Utility function to remove AI wavy underline styling from HTML content
 * This ensures that wavy underlines used for AI analysis in the editor
 * don't appear in published content
 */
export function cleanWavyUnderlines(content: string): string {
  if (!content || typeof content !== 'string') {
    return content;
  }

  const propertiesToRemove = [
    'text-decoration',
    'text-decoration-style',
    'text-decoration-line',
    'text-decoration-color',
    'text-decoration-thickness',
    'text-underline-offset',
  ];

  const cleanStyleAttribute = (style: string): string => {
    const cleaned = style
      .split(';')
      .map(part => part.trim())
      .filter(Boolean)
      .filter(part => {
        const [property] = part.split(':').map(token => token.trim().toLowerCase());
        return property ? !propertiesToRemove.includes(property) : true;
      });

    return cleaned.join('; ');
  };

  let cleanedContent = content;

  // Unwrap underline tags that wrap the AI highlight wrapper
  cleanedContent = cleanedContent.replace(
    /<u\b[^>]*>\s*(<span\b[^>]*data-wavy-underline[^>]*>[\s\S]*?<\/span>)\s*<\/u>/gi,
    (_match, spanContent) => spanContent
  );

  cleanedContent = cleanedContent
    // Process spans that carry the wavy underline marker
    .replace(/<span\b([^>]*)>([\s\S]*?)<\/span>/gi, (match, rawAttributes = '', innerHtml = '') => {
      if (!/data-wavy-underline/i.test(rawAttributes)) {
        return match;
      }

      let attributes = rawAttributes;

      // Remove data attributes used for wavy underline styling
      attributes = attributes
        .replace(/\sdata-wavy-underline(?:=(["']).*?\1)?/gi, '')
        .replace(/\sdata-color(?:=(["']).*?\1)?/gi, '');

      // Clean the style attribute on the span (double or single quotes)
      attributes = attributes.replace(/\sstyle=(["'])(.*?)\1/gi, (_styleMatch, quote: string, styleContent: string) => {
        const cleanedStyle = cleanStyleAttribute(styleContent);
        if (!cleanedStyle) {
          return '';
        }
        return ` style=${quote}${cleanedStyle}${quote}`;
      });

      const cleanedInnerHtml = innerHtml.replace(/<\/?u\b[^>]*>/gi, '');

      const trimmedAttributes = attributes.trim();
      if (!trimmedAttributes) {
        return cleanedInnerHtml;
      }

      return `<span${trimmedAttributes.startsWith(' ') ? '' : ' '}${trimmedAttributes}>${cleanedInnerHtml}</span>`;
    })
    // As a safeguard, strip any remaining data-wavy-underline/data-color attributes outside of spans
    .replace(/\sdata-wavy-underline(?:=(["']).*?\1)?/gi, '')
    .replace(/\sdata-color(?:=(["']).*?\1)?/gi, '')
    // Remove empty style attributes that might have been left behind
    .replace(/\sstyle=(["'])(?:\s*;?\s*)\1/gi, '')
    // Clean up spans that became empty after attribute removal
    .replace(/<span[^>]*>\s*<\/span>/gi, '');

  // Remove wavy underline related style declarations from any remaining elements
  cleanedContent = cleanedContent.replace(/\sstyle=(["'])([^"']*?(?:text-decoration|text-underline-offset)[^"']*)\1/gi, (match, quote, styleContent) => {
    const lowerStyle = styleContent.toLowerCase();
    const highlightStylePattern = /(text-decoration-style\s*:\s*wavy|text-decoration-color|text-underline-offset|text-decoration\s*:[^;]*wavy)/i;
    if (!highlightStylePattern.test(lowerStyle)) {
      return match;
    }

    const cleanedStyle = cleanStyleAttribute(styleContent);
    if (!cleanedStyle) {
      return '';
    }

    return ` style=${quote}${cleanedStyle}${quote}`;
  });

  return cleanedContent;
}
